import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { FiTrash2, FiPlus, FiTruck, FiTag, FiX, FiEdit } from "react-icons/fi";
import { useAddresses } from "../hooks/useAddresses";
import { useCart } from "../hooks/useCart";
import { useOrders } from "../hooks/useOrders";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { supabase } from "../config/supabase";
import { getPaymentReturnOrigin } from "../utils/paymentReturnOrigin";
import { orderConfirmationPath } from "../utils/orderReference";

const GCC_CURRENCY_BY_COUNTRY = {
  oman: "OMR",
  "united arab emirates": "AED",
  uae: "AED",
  saudi: "SAR",
  "saudi arabia": "SAR",
  kuwait: "KWD",
  qatar: "QAR",
  bahrain: "BHD",
};

const getCurrencyByCountry = (countryName) => {
  const normalized = String(countryName || "").trim().toLowerCase();
  return GCC_CURRENCY_BY_COUNTRY[normalized] || "OMR";
};

const parseNonNegative = (value, fallback = 0) => {
  const parsed = parseFloat(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return parsed;
};

/** Align checkout display and payment (OMR baisa = 3 decimal places). */
const roundOmr = (n) => Math.round((Number(n) || 0) * 1000) / 1000;

const REQUIRED_ADDRESS_FIELDS = [
  { key: "full_name", label: "Full Name", labelAr: "الاسم الكامل" },
  { key: "phone_number", label: "Phone Number", labelAr: "رقم الهاتف" },
  { key: "area", label: "Area / Street", labelAr: "المنطقة / الشارع" },
  { key: "city", label: "City", labelAr: "المدينة" },
  { key: "governorate", label: "Governorate", labelAr: "المحافظة" },
  { key: "country", label: "Country", labelAr: "الدولة" },
];

const getMissingAddressFields = (address = {}) =>
  REQUIRED_ADDRESS_FIELDS.filter(
    ({ key }) => String(address?.[key] ?? "").trim() === ""
  );

const getMissingFieldLabels = (fields = [], isArabic = false) =>
  fields.map((field) => (isArabic ? field.labelAr : field.label)).join(", ");

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 24px 20px;

  @media (max-width: 768px) {
    padding: 16px 14px;
  }

  @media (max-width: 480px) {
    padding: 12px 10px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    margin-bottom: 16px;
  }
`;

const CheckoutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 32px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const FormSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 16px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #34d399;
  }
`;


const Textarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #34d399;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;

  input {
    cursor: pointer;
  }
`;

const Sidebar = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  height: fit-content;
  position: sticky;
  top: 100px;

  @media (max-width: 968px) {
    position: static;
    top: auto;
    padding: 16px;
  }
`;

const OrderItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 480px) {
    gap: 10px;
    align-items: flex-start;
  }
`;

const ItemImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;

  @media (max-width: 480px) {
    width: 52px;
    height: 52px;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
`;

const ItemMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const ItemPrice = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #34d399;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  font-size: 16px;
  color: ${(props) => (props.bold ? "#1f2937" : "#6b7280")};
  font-weight: ${(props) => (props.bold ? "600" : "400")};
  border-top: ${(props) => (props.divider ? "2px solid #e5e7eb" : "none")};

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 10px 0;
  }
`;

// const PaymentMethod = styled.div`
//   border: 2px solid ${(props) => (props.selected ? "#34D399" : "#e5e7eb")};
//   border-radius: 8px;
//   padding: 16px;
//   margin-bottom: 12px;
//   cursor: pointer;
//   transition: all 0.2s;

//   &:hover {
//     border-color: #34d399;
//   }
// `;

// const PaymentLabel = styled.label`
//   display: flex;
//   align-items: center;
//   gap: 12px;
//   cursor: pointer;
//   font-weight: 500;
//   margin: 0;
// `;

const PlaceOrderButton = styled.button`
  width: 100%;
  padding: 16px;
  background: #5efba9;
  color: #374151;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 24px;

  &:hover:not(:disabled) {
    background: #4ce89a;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    margin-top: 16px;
    padding: 14px;
    font-size: 15px;
  }
`;

// Skeleton loading components
const SkeletonBox = styled.div`
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #f8f8f8 50%,
    #f0f0f0 100%
  );
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 6px;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const SkeletonImage = styled(SkeletonBox)`
  width: 60px;
  height: 60px;
  border-radius: 6px;
`;

const SkeletonText = styled(SkeletonBox)`
  height: ${props => props.height || '16px'};
  width: ${props => props.width || '100%'};
  margin-bottom: ${props => props.marginBottom || '8px'};
`;

const SkeletonRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  align-items: center;
`;

const AddressManager = styled.div`
  margin-bottom: 24px;
`;

const AddressList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AddressCard = styled.div`
  border: 2px solid ${(props) => (props.selected ? "#5efba9" : "#e5e7eb")};
  border-radius: 8px;
  padding: 16px;
  position: relative;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${(props) => (props.selected ? "#5efba9" : "#d1d5db")};
  }

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const AddressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 8px;
  gap: 8px;
`;

const AddressTitle = styled.div`
  font-weight: 600;
  color: #1f2937;
`;

const AddressActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #6b7280;
  display: flex;
  align-items: center;

  &:hover {
    color: #5efba9;
  }
`;

const AddressDetails = styled.div`
  color: #6b7280;
  font-size: 14px;
  line-height: 1.6;

  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 1.45;
  }
`;

const AddNewAddressButton = styled.button`
  width: 100%;
  padding: 12px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  background: transparent;
  color: #6b7280;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #5efba9;
    color: #5efba9;
  }
`;

const VoucherSection = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
`;

const VoucherInputGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const VoucherInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  text-transform: uppercase;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
`;

const VoucherButton = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(props) => (props.remove ? "#fee2e2" : "#5efba9")};
  color: ${(props) => (props.remove ? "#991b1b" : "#1f2937")};

  &:hover:not(:disabled) {
    background: ${(props) => (props.remove ? "#fecaca" : "#4de8a8")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    /* Full-width only for Apply in the input row; never for icon-only remove */
    width: ${(p) => (p.$fullWidthOnMobile ? "100%" : "auto")};
  }
`;

const VoucherSuccess = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #d1fae5;
  border: 1px solid #86efac;
  border-radius: 6px;
  color: #065f46;
  font-size: 14px;
  font-weight: 500;
  margin-top: 8px;
  min-width: 0;
`;

const VoucherSuccessMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const VoucherSuccessText = styled.span`
  flex: 1;
  min-width: 0;
  word-break: break-word;
`;

const VoucherSuccessRemove = styled.button`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #fee2e2;
  color: #991b1b;
  transition: background 0.2s;

  &:hover {
    background: #fecaca;
  }

  &:focus-visible {
    outline: 2px solid #991b1b;
    outline-offset: 2px;
  }
`;

const VoucherError = styled.div`
  padding: 10px 12px;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #991b1b;
  font-size: 13px;
  margin-top: 8px;
`;

function Checkout() {
  const navigate = useNavigate();
  const { isArabic } = useLanguage();
  const {
    addresses,
    loading: addressesLoading,
    addAddress,
    updateAddress,
    deleteAddress,
    getDefaultAddress,
  } = useAddresses();
  const { cartItems, cart } = useCart();
  const { createOrderFromCart, markOrderPaid } = useOrders();
  // const [paymentMethod, setPaymentMethod] = useState("card");
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [placingOrderMessage, setPlacingOrderMessage] = useState('');
  const [formData, setFormData] = useState({
    full_name: "",
    phone_number: "",
    area: "",
    city: "",
    governorate: "",
    country: "",
    delivery_notes: "",
    is_default: false,
  });
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [shippingFee, setShippingFee] = useState(0);
  const [shippingRate, setShippingRate] = useState(null);
  const [calculatingShipping, setCalculatingShipping] = useState(true);
  const [shippingError, setShippingError] = useState(null);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [skeletonStartTime, setSkeletonStartTime] = useState(Date.now());
  const [shippingCountries, setShippingCountries] = useState([]);

  // Voucher state
  const [voucherCode, setVoucherCode] = useState("");
  const [appliedVoucher, setAppliedVoucher] = useState(null);
  const [voucherError, setVoucherError] = useState(null);
  const [applyingVoucher, setApplyingVoucher] = useState(false);
  const [voucherDiscount, setVoucherDiscount] = useState(0);
  const [paymentMethod] = useState("card");
  // eslint-disable-next-line no-unused-vars
  const [paymentError, setPaymentError] = useState(null);


  // Set selected address to default or first address when addresses load
  React.useEffect(() => {
    if (addresses.length > 0 && !selectedAddress) {
      const defaultAddr = getDefaultAddress();
      const addressId = defaultAddr?.id || addresses[0].id;
      console.log(
        "Setting selected address:",
        addressId,
        "Type:",
        typeof addressId
      );
      console.log("Default address:", defaultAddr);
      console.log("First address:", addresses[0]);

      // Ensure address ID is stored as string to maintain UUID format
      setSelectedAddress(String(addressId));
    }
  }, [addresses, selectedAddress, getDefaultAddress]);



  // Manage skeleton loading with minimum 2 second display
  useEffect(() => {
    if (calculatingShipping) {
      setShowSkeleton(true);
      setSkeletonStartTime(Date.now());
    } else {
      // Ensure skeleton shows for at least 2 seconds
      const elapsed = Date.now() - skeletonStartTime;
      const remaining = Math.max(0, 2000 - elapsed);
      
      if (remaining > 0) {
        const timer = setTimeout(() => {
          setShowSkeleton(false);
        }, remaining);
        return () => clearTimeout(timer);
      } else {
        setShowSkeleton(false);
      }
    }
  }, [calculatingShipping, skeletonStartTime]);

  // Fetch default shipping rate and calculate shipping
  useEffect(() => {
    const fetchShippingRate = async () => {
      try {
        setCalculatingShipping(true);
        setShippingError(null);
        setSkeletonStartTime(Date.now());
        console.log("🚚 Fetching default shipping rate...");

        // Fetch default shipping rate from view
        const { data: shippingCountriesData, error: shippingCountriesError } = await supabase
          .from("shipping_countries")
          .select("*");

        if (shippingCountriesError) {
          console.warn("Failed to load shipping countries:", shippingCountriesError);
          setShippingCountries([]);
          setShippingRate(null);
          setShippingFee(0);
          setShippingError("Failed to load shipping countries. Please refresh and try again.");
          setCalculatingShipping(false);
          return;
        }

        setShippingCountries(shippingCountriesData || []);


        const selectedAddr = addresses.find(
          (addr) => String(addr.id) === String(selectedAddress)
        );

        if (!selectedAddr) {
          console.warn("No selected address found");
          setShippingRate(null);
          setShippingFee(0);
          setShippingError("Please select a shipping address to calculate shipping.");
          setCalculatingShipping(false);
          return;
        }

        const normalize = (v) => String(v || "").trim().toLowerCase();
        const selectedCountryRaw = selectedAddr?.country;
        const selectedCountryNorm = normalize(selectedCountryRaw);
        const selectCountry = shippingCountriesData?.find((country) => {
          const countryName = normalize(country.name);
          const countryCode = normalize(country.code);
          const countryId = normalize(country.id);
          // Support addresses storing country as name, code, or id
          return (
            selectedCountryNorm === countryName ||
            selectedCountryNorm === countryCode ||
            selectedCountryNorm === countryId
          );
        });

        if (!selectCountry) {
          console.warn("No shipping country found for address country:", selectedAddr.country);
          setShippingRate(null);
          setShippingFee(0);
          setShippingError("Shipping is not available for the selected country.");
          setCalculatingShipping(false);
          return;
        }

        // Fetch default shipping rate from view
        const { data: defaultRate, error: rateError } = await supabase
          .from("shipping_rates")
          .select(`
            *,
            shipping_countries(id, code, name)
          `)
          .eq("country_id", selectCountry.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        console.log("Default shipping rate:", defaultRate, "error:", rateError);

        if (rateError || !defaultRate) {
          console.warn("No default shipping rate found, using 0");
          setShippingRate(null);
          setShippingFee(0);
          setShippingError("No shipping rate found for the selected country.");
          setCalculatingShipping(false);
          return;
        }

        setShippingRate(defaultRate);

        // Calculate total cart weight
        let totalWeight = 0;

        // Fetch products to get their weights
        if (cartItems && cartItems.length > 0) {
          const productIds = cartItems.map((item) => item.product_id);
          const { data: products, error: productsError } = await supabase
            .from("products")
            .select("id, weight_kg")
            .in("id", productIds);

          console.log("Products with weights:", products);

          if (!productsError && products) {
            cartItems.forEach((item) => {
              const product = products.find((p) => p.id === item.product_id);
              const weight = parseNonNegative(product?.weight_kg, 0);
              const quantity = parseInt(item.quantity || 1);
              totalWeight += weight * quantity;
            });
          }
        }

        console.log("Total cart weight:", totalWeight, "kg");

        // Calculate shipping fee using the formula
        const basePrice = parseNonNegative(defaultRate.base_price, 0);
        const baseWeight = parseNonNegative(defaultRate.base_weight_kg, 1.0);
        const extraPerKg = parseNonNegative(defaultRate.extra_per_kg, 0);

        console.log("..................", selectedAddress);

        let calculatedShipping = 0;
        if (totalWeight <= baseWeight) {
          calculatedShipping = basePrice;
        } else {
          const extraWeight = totalWeight - baseWeight;
          calculatedShipping = basePrice + Math.ceil(extraWeight) * extraPerKg;
        }

        console.log("Shipping calculation:", {
          totalWeight,
          basePrice,
          baseWeight,
          extraPerKg,
          calculatedShipping,
        });

        setShippingFee(calculatedShipping);
        setShippingError(null);
      } catch (error) {
        console.error("Error fetching shipping rate:", error);
        setShippingFee(0);
        setShippingRate(null);
        setShippingError("Failed to calculate shipping. Please try again.");
      } finally {
        setCalculatingShipping(false);
      }
    };

    if (cartItems && cartItems.length > 0) {
      fetchShippingRate();
    } else {
      setShippingFee(0);
      setCalculatingShipping(false);
      // Still show skeleton for 2 seconds even if no items
      setSkeletonStartTime(Date.now());
      setTimeout(() => {
        setShowSkeleton(false);
      }, 2000);
    }
  }, [cartItems, selectedAddress, addresses]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveAddress = async () => {
    const missingFields = getMissingAddressFields(formData);
    if (missingFields.length > 0) {
      alert(
        isArabic
          ? `يرجى إكمال جميع الحقول المطلوبة: ${getMissingFieldLabels(missingFields, true)}`
          : `Please complete all required fields: ${getMissingFieldLabels(missingFields)}`
      );
      return;
    }

    try {
      console.log("Saving address with data:", formData);
      if (editingAddressId) {
        await updateAddress(editingAddressId, formData);
        setEditingAddressId(null);
      } else {
      await addAddress(formData);
      }
      setIsAddingAddress(false);
      setFormData({
        full_name: "",
        phone_number: "",
        area: "",
        city: "",
        governorate: "",
        country: "",
        delivery_notes: "",
        is_default: false,
      });
      alert("Address saved successfully!");
    } catch (error) {
      console.error("Error saving address:", error);
      console.error("Error details:", error.message, error);
      alert(`Failed to save address: ${error.message || "Please try again."}`);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm("Are you sure you want to delete this address?")) {
      return;
    }
    try {
      // If user deletes the currently selected address, reset selection to prevent stale checkout
      if (String(addressId) === String(selectedAddress)) {
        setSelectedAddress(null);
      }
      await deleteAddress(addressId);
    } catch (error) {
      console.error("Error deleting address:", error);
      alert("Failed to delete address. Please try again.");
    }
  };

  /**
   * Uses DB RPC validate_voucher_for_cart (SECURITY DEFINER) so scope + product joins
   * are not blocked by RLS for some shoppers (same pattern as cart_get).
   */
  const calculateVoucherDiscountForCart = async (voucher) => {
    const code = (voucher?.code || "").trim();
    if (!code) {
      throw new Error("Invalid voucher");
    }

    const { data, error } = await supabase.rpc("validate_voucher_for_cart", {
      p_code: code,
    });

    if (error) {
      throw new Error(error.message || "Failed to validate voucher");
    }

    if (!data || !data.success) {
      throw new Error(data?.error || "This voucher doesn't apply to any items in your cart");
    }

    const discount = parseFloat(data.discount);
    const applicableSubtotal = parseFloat(data.applicable_subtotal);
    const applicableItemsCount = Number(data.applicable_items_count) || 0;

    return {
      discount: roundOmr(Number.isFinite(discount) ? discount : 0),
      applicableItemsCount,
      applicableSubtotal: roundOmr(Number.isFinite(applicableSubtotal) ? applicableSubtotal : 0),
      voucherSnapshot: data.voucher || null,
    };
  };

  const handleApplyVoucher = async () => {
    if (!voucherCode.trim()) {
      setVoucherError("Please enter a voucher code");
      return;
    }

    try {
      setApplyingVoucher(true);
      setVoucherError(null);

      const { discount, applicableItemsCount, applicableSubtotal, voucherSnapshot } =
        await calculateVoucherDiscountForCart({ code: voucherCode });

      const voucherRow = voucherSnapshot;
      if (!voucherRow || !voucherRow.code) {
        throw new Error("Invalid or expired voucher code");
      }

      setAppliedVoucher(voucherRow);
      setVoucherDiscount(discount);
      setVoucherCode("");
      console.log("✅ Voucher applied:", {
        voucher: voucherRow,
        discount,
        applicableItemsCount,
        totalCartItems: cartItems.length,
        applicableSubtotal,
        scope: voucherRow.applies_to,
      });
    } catch (err) {
      console.error("Error applying voucher:", err);
      setVoucherError(err.message);
    } finally {
      setApplyingVoucher(false);
    }
  };

  const handleRemoveVoucher = () => {
    setAppliedVoucher(null);
    setVoucherDiscount(0);
    setVoucherError(null);
  };

  // Keep voucher discount in sync when cart changes (quantity/new items).
  useEffect(() => {
    let cancelled = false;

    const recalculateVoucher = async () => {
      if (!appliedVoucher) return;
      try {
        const { discount } = await calculateVoucherDiscountForCart(appliedVoucher);
        if (!cancelled) {
          setVoucherDiscount(discount);
          setVoucherError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setVoucherDiscount(0);
          setVoucherError(err.message || "Voucher no longer applies to current cart");
        }
      }
    };

    recalculateVoucher();
    return () => {
      cancelled = true;
    };
  }, [appliedVoucher, cartItems]);

  const handlePlaceOrder = async () => {
    try {
      // Validation
      if (!cart || cartItems.length === 0) {
        alert("Your cart is empty. Please add items before placing an order.");
        return;
      }

      if (!selectedAddress) {
        alert("Please select a shipping address.");
        return;
      }

      if (addresses.length === 0) {
        alert("No shipping addresses found. Please add an address first.");
        return;
      }

      // CRITICAL: Wait for shipping calculation to complete before placing order
      if (calculatingShipping) {
        console.log("⏳ Shipping calculation in progress, waiting...");
        alert("Please wait for shipping calculation to complete before placing your order.");
        return;
      }

      // CRITICAL: Must have a valid shipping rate record (free rates are allowed)
      if (!shippingRate) {
        alert(shippingError || "Shipping is not available for the selected address. Please select a different address/country.");
        return;
      }

      setIsPlacingOrder(true);
      setPaymentError(null);
      
      console.log("🚚 Current shipping fee:", shippingFee);

      console.log(
        "Placing order with cart:",
        cart.id,
        "address:",
        selectedAddress
      );
      console.log("Available addresses:", addresses);

      // Validate selected address
      const selectedAddr = addresses.find(
        (addr) => String(addr.id) === String(selectedAddress)
      );
      if (!selectedAddr) {
        console.error("Selected address not found in addresses list");
        alert("Selected address not found. Please select a different address.");
        setIsPlacingOrder(false);
        return;
      }

      const missingAddressFields = getMissingAddressFields(selectedAddr);
      if (missingAddressFields.length > 0) {
        alert(
          isArabic
            ? `العنوان المحدد غير مكتمل. يرجى تعديله وإكمال: ${getMissingFieldLabels(missingAddressFields, true)}`
            : `Selected address is incomplete. Please edit it and fill: ${getMissingFieldLabels(missingAddressFields)}`
        );
        setIsPlacingOrder(false);
        return;
      }

      console.log("Using address:", selectedAddr);
      const addressId = String(selectedAddr.id);
      console.log("Passing address ID:", addressId);

      // Double-check shipping calculation is complete (fail-safe)
      if (calculatingShipping) {
        console.error("❌ Shipping calculation still in progress - this should not happen!");
        alert("Shipping calculation error. Please refresh the page and try again.");
        setIsPlacingOrder(false);
        return;
      }

      // Same rounding as createOrderFromCart / Thawani (OMR 3dp)
      const shipR = roundOmr(shippingFee);
      const totalBeforeFee = roundOmr(subtotal + shipR - voucherDiscount);
      const onlinePaymentFee = roundOmr(totalBeforeFee * 0.02);

      // Log all totals to verify correct calculation for payment gateway
      console.log("💰 ========== ORDER TOTALS FOR PAYMENT GATEWAY ==========");
      console.log("  Subtotal:", subtotal.toFixed(3), selectedCurrency);
      console.log("  Shipping Fee:", shipR.toFixed(3), selectedCurrency);
      console.log("  Voucher Discount:", voucherDiscount.toFixed(3), selectedCurrency);
      console.log("  Total Before Fee:", totalBeforeFee.toFixed(3), selectedCurrency);
      console.log("  Online Payment Fee (2%):", onlinePaymentFee.toFixed(3), selectedCurrency);
      console.log("  Grand Total:", roundOmr(totalBeforeFee + onlinePaymentFee).toFixed(3), selectedCurrency);
      console.log("💰 ====================================================");

      // First, create the order in the database
      // This will also send order confirmation emails immediately (customer and store owner)
      console.log("📧 Order creation will trigger email notifications immediately...");
      const created = await createOrderFromCart(
        cart.id,
        addressId,
        shippingFee,
        voucherDiscount,
        0,
        onlinePaymentFee,
        selectedCurrency,
        appliedVoucher?.code ?? null
      );
      const orderId = created.id;

      console.log("✅ Order created with ID:", orderId, "order_number:", created.order_number);
      console.log("📧 Email notifications have been sent (customer confirmation + store owner notification)");

      // Discount and grand_total are already set on insert. Do not call apply_voucher_to_order here:
      // it often recomputes totals and overwrites grand_total before Thawani, so the gateway amount
      // no longer matched checkout. Record voucher_code on the order via createOrderFromCart instead.

      // Handle payment based on selected method
      if (paymentMethod === "card") {
        // Create payment session with Thawani
        // The payment amount will be taken from order.grand_total which includes the payment fee
        const { data, error } = await supabase.functions.invoke('create-payment-session', {
          body: {
            orderId,
            customerEmail: (await supabase.auth.getUser())?.data?.user?.email || null,
            voucher_code: appliedVoucher ? appliedVoucher.code : null,
            origin: getPaymentReturnOrigin(),
          },
        });

        if (error) {
          throw new Error(error.message || "Failed to create payment session");
        }

        // Redirect to Thawani payment page
        window.location.href = data.paymentUrl;

      } else {
        // For other payment methods (e.g., cash on delivery)
        // Mark order as paid directly
        await markOrderPaid(orderId, `cash-${Date.now()}`, (phase) => {
          if (phase === 'recording') {
            setPlacingOrderMessage(isArabic ? 'جاري تأكيد الدفع…' : 'Recording payment…');
          }
        });

        navigate(orderConfirmationPath(created));
      }
    } catch (error) {
      console.error("Error placing order:", error);
      alert(`Failed to place order: ${error.message || "Please try again."}`);
    } finally {
      setIsPlacingOrder(false);
      setPlacingOrderMessage('');
    }
  };

  // Calculate subtotal from cart items (prices already have discounts applied)
  const subtotal = roundOmr(
    cartItems.reduce((sum, item) => {
      const price = parseFloat(item.price || 0);
      return sum + price * item.quantity;
    }, 0)
  );

  const selectedAddressData = addresses.find(
    (addr) => String(addr.id) === String(selectedAddress)
  );
  const selectedAddressMissingFields = selectedAddressData
    ? getMissingAddressFields(selectedAddressData)
    : [];
  const selectedCurrency = (
    shippingRate?.currency ||
    getCurrencyByCountry(selectedAddressData?.country)
  )
    .toString()
    .toUpperCase();

  const shippingRounded = roundOmr(shippingFee);
  // Calculate total before payment fee
  const totalBeforeFee = roundOmr(subtotal + shippingRounded - voucherDiscount);

  // Calculate 2% online payment fee (on total including shipping)
  const onlinePaymentFee = roundOmr(totalBeforeFee * 0.02);

  // Final total including payment fee
  const total = roundOmr(totalBeforeFee + onlinePaymentFee);

  if (addressesLoading) {
    return (
      <Container>
        <Title>Checkout</Title>
        <div
          style={{
            textAlign: "center",
            padding: "64px 24px",
            color: "#6b7280",
          }}
        >
          Loading checkout...
        </div>
      </Container>
    );
  }



  return (
    <Container>
      <Title>{isArabic ? 'الدفع' : 'Checkout'}</Title>

      <CheckoutContent>
        <div>
          <FormSection>
            <SectionTitle>{isArabic ? 'عنوان الشحن' : 'Shipping Address'}</SectionTitle>

            {addresses.length > 0 && (
              <AddressManager>
                <Label>Select Saved Address</Label>
                <AddressList>
                  {addresses.map((address) => (
                    <AddressCard
                      key={address.id}
                      selected={selectedAddress === String(address.id)}
                      onClick={() => setSelectedAddress(String(address.id))}
                    >
                      <AddressHeader>
                        <AddressTitle>
                          {address.full_name || address.phone_number}
                          {address.is_default && (
                            <span
                              style={{
                                color: "#5efba9",
                                fontSize: "12px",
                                marginLeft: "8px",
                              }}
                            >
                              (Default)
                            </span>
                          )}
                        </AddressTitle>
                        <AddressActions onClick={(e) => e.stopPropagation()}>
                          <ActionButton
                            onClick={() => {
                              setEditingAddressId(address.id);
                              setIsAddingAddress(true);
                              setFormData({
                                full_name: address.full_name || "",
                                phone_number: address.phone_number || "",
                                area: address.area || "",
                                city: address.city || "",
                                governorate: address.governorate || "",
                                country: address.country || "",
                                delivery_notes: address.delivery_notes || "",
                                is_default: address.is_default || false,
                              });
                            }}
                            style={{ marginRight: '8px' }}
                          >
                            <FiEdit />
                          </ActionButton>
                          <ActionButton
                            onClick={() => handleDeleteAddress(address.id)}
                          >
                            <FiTrash2 />
                          </ActionButton>
                        </AddressActions>
                      </AddressHeader>
                      <AddressDetails>
                        <div>{address.phone_number}</div>
                        <div>
                          {address.area}, {address.city}, {address.governorate}
                          {address.country ? `, ${address.country}` : ""}
                        </div>
                        {address.delivery_notes && (
                          <div style={{ marginTop: "4px" }}>
                            <em>{address.delivery_notes}</em>
                          </div>
                        )}
                      </AddressDetails>
                    </AddressCard>
                  ))}
                </AddressList>
              </AddressManager>
            )}

            {!isAddingAddress ? (
              <AddNewAddressButton onClick={() => {
                setEditingAddressId(null);
                setIsAddingAddress(true);
                setFormData({
                  full_name: "",
                  phone_number: "",
                  area: "",
                  city: "",
                  governorate: "",
                  country: "",
                  delivery_notes: "",
                  is_default: false,
                });
              }}>
                <FiPlus /> Add New Address
              </AddNewAddressButton>
            ) : (
              <>
                <FormGroup>
                  <Label>Full Name *</Label>
                  <Input
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Phone Number *</Label>
                  <Input
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    type="tel"
                    placeholder="+968 XXXX XXXX"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label required>{isArabic ? 'البلد *' : 'Country *'}</Label>
                  <Input
                    as="select"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    required
                    style={{
                      padding: '12px 16px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">{isArabic ? 'اختر البلد' : 'Select country'}</option>
                    {shippingCountries.map((country) => (
                      <option key={country.code} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Label>Area / Street *</Label>
                  <Input
                    name="area"
                    value={formData.area}
                    onChange={handleInputChange}
                    type="text"
                    placeholder="Enter area or street name"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>City *</Label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    type="text"
                    placeholder="Enter city"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Governorate *</Label>
                  <Input
                    name="governorate"
                    value={formData.governorate}
                    onChange={handleInputChange}
                    type="text"
                    placeholder="Enter governorate"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Delivery Notes (Optional)</Label>
                  <Textarea
                    name="delivery_notes"
                    value={formData.delivery_notes}
                    onChange={handleInputChange}
                    placeholder="Any special instructions for delivery"
                  />
                </FormGroup>
                <FormGroup>
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={formData.is_default}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          is_default: e.target.checked,
                        }))
                      }
                    />
                    <span>Set as default address</span>
                  </CheckboxLabel>
                </FormGroup>
                <div style={{ display: "flex", gap: "12px" }}>
                  <PlaceOrderButton
                    onClick={handleSaveAddress}
                    style={{ flex: 1 }}
                  >
                    {editingAddressId ? (isArabic ? 'تحديث العنوان' : 'Update Address') : (isArabic ? 'حفظ العنوان' : 'Save Address')}
                  </PlaceOrderButton>
                  <PlaceOrderButton
                    onClick={() => {
                      setIsAddingAddress(false);
                      setEditingAddressId(null);
                      setFormData({
                        full_name: "",
                        phone_number: "",
                        area: "",
                        city: "",
                        governorate: "",
                        country: "",
                        delivery_notes: "",
                        is_default: false,
                      });
                    }}
                    style={{ flex: 1, background: "#6b7280" }}
                  >
                    {isArabic ? 'إلغاء' : 'Cancel'}
                  </PlaceOrderButton>
                </div>
              </>
            )}
          </FormSection>

          {/* Voucher Section */}
          <FormSection>
            <SectionTitle>{isArabic ? 'لديك رمز خصم؟' : 'Have a Promo Code?'}</SectionTitle>
            {!appliedVoucher ? (
              <VoucherSection>
                <VoucherInputGroup>
                  <VoucherInput
                    type="text"
                    placeholder={isArabic ? 'أدخل رمز الخصم' : 'Enter voucher code'}
                    value={voucherCode}
                    onChange={(e) => {
                      setVoucherCode(e.target.value);
                      setVoucherError(null);
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleApplyVoucher();
                      }
                    }}
                  />
                  <VoucherButton
                    $fullWidthOnMobile
                    onClick={handleApplyVoucher}
                    disabled={applyingVoucher}
                  >
                    {applyingVoucher ? (isArabic ? 'جاري التطبيق...' : 'Applying...') : (isArabic ? 'تطبيق' : 'Apply')}
                  </VoucherButton>
                </VoucherInputGroup>
                {voucherError && <VoucherError>{voucherError}</VoucherError>}
              </VoucherSection>
            ) : (
              <VoucherSuccess dir={isArabic ? "rtl" : "ltr"}>
                <VoucherSuccessMain>
                  <FiTag aria-hidden />
                  <VoucherSuccessText>
                    <strong>{appliedVoucher.code}</strong>
                    {isArabic ? " مُطبق — " : " applied — "}
                    {appliedVoucher.vtype === "percent"
                      ? `${appliedVoucher.amount}% ${isArabic ? "خصم" : "off"}`
                      : `${appliedVoucher.amount} ${appliedVoucher.currency} ${isArabic ? "خصم" : "off"}`}
                  </VoucherSuccessText>
                </VoucherSuccessMain>
                <VoucherSuccessRemove
                  type="button"
                  onClick={handleRemoveVoucher}
                  aria-label={isArabic ? "إزالة رمز الخصم" : "Remove voucher"}
                >
                  <FiX />
                </VoucherSuccessRemove>
              </VoucherSuccess>
            )}
          </FormSection>
        </div>

        <Sidebar>
          <SectionTitle>{isArabic ? 'ملخص الطلب' : 'Order Summary'}</SectionTitle>

          {showSkeleton ? (
            <>
              {/* Skeleton Loading for Order Items */}
              {cartItems.map((item, idx) => (
                <SkeletonItem key={idx}>
                  <SkeletonImage />
                  <div style={{ flex: 1 }}>
                    <SkeletonText height="16px" width="70%" marginBottom="8px" />
                    <SkeletonText height="12px" width="40%" />
                  </div>
                  <SkeletonText height="16px" width="60px" />
                </SkeletonItem>
              ))}

              {/* Skeleton Loading for Summary Rows */}
              <SkeletonRow>
                <SkeletonText height="16px" width="100px" />
                <SkeletonText height="16px" width="80px" />
              </SkeletonRow>
              <SkeletonRow>
                <SkeletonText height="16px" width="120px" />
                <SkeletonText height="16px" width="100px" />
              </SkeletonRow>
              <SkeletonRow>
                <SkeletonText height="16px" width="150px" />
                <SkeletonText height="16px" width="80px" />
              </SkeletonRow>
              <SkeletonRow style={{ paddingTop: '16px', borderTop: '2px solid #e5e7eb' }}>
                <SkeletonText height="18px" width="80px" />
                <SkeletonText height="18px" width="100px" />
              </SkeletonRow>

              {/* Skeleton Loading for Button */}
              <SkeletonBox
                style={{
                  height: '52px',
                  marginTop: '24px',
                  borderRadius: '8px'
                }}
              />
            </>
          ) : (
            <>
              {cartItems.map((item) => {
                const product = item.product;
                return (
                  <OrderItem key={item.id}>
                    <ItemImage
                      src={product?.images?.[0] || "https://via.placeholder.com/60"}
                      alt={product?.name}
                    />
                    <ItemInfo>
                      <ItemTitle>{product?.name}</ItemTitle>
                      <ItemMeta>Qty: {item.quantity}</ItemMeta>
                    </ItemInfo>
                    <ItemPrice>{item.price.toFixed(2)} {selectedCurrency}</ItemPrice>
                  </OrderItem>
                );
              })}

              <SummaryRow>
                <span>{isArabic ? 'المجموع الفرعي' : 'Subtotal'}</span>
                <span>{subtotal.toFixed(3)} {selectedCurrency}</span>
              </SummaryRow>
              {voucherDiscount > 0 && (
                <SummaryRow>
                  <span>
                    <FiTag
                      style={{
                        display: "inline",
                        marginRight: "4px",
                        verticalAlign: "middle",
                      }}
                    />
                    {isArabic ? 'الخصم' : 'Discount'} ({appliedVoucher?.code})
                  </span>
                  <span style={{ color: "#059669", fontWeight: 600 }}>
                    -{voucherDiscount.toFixed(3)} {selectedCurrency}
                  </span>
                </SummaryRow>
              )}
              <SummaryRow>
                <span>
                  <FiTruck
                    style={{
                      display: "inline",
                      marginRight: "4px",
                      verticalAlign: "middle",
                    }}
                  />
                  {isArabic ? 'الشحن' : 'Shipping'}
                </span>
                <span>{shippingRounded.toFixed(3)} {selectedCurrency}</span>
              </SummaryRow>
              {shippingRate && (
                <div
                  style={{
                    fontSize: "11px",
                    color: "#6b7280",
                    padding: "8px 12px",
                    background: "#f9fafb",
                    borderRadius: "6px",
                    marginTop: "8px",
                  }}
                >
                  <strong>{isArabic ? 'الشحن إلى:' : 'Shipping to:'}</strong> {shippingRate.shipping_countries?.name || (isArabic ? 'غير محدد' : 'Not specified')} (
                  {shippingRate.shipping_countries?.code || ''})
                  <br />
                  <strong>{isArabic ? 'السعر الأساسي:' : 'Base Rate:'}</strong> {shippingRate.currency}{" "}
                  {parseFloat(shippingRate.base_price).toFixed(3)} (0-
                  {parseFloat(shippingRate.base_weight_kg).toFixed(1)}kg)
                  {parseFloat(shippingRate.extra_per_kg) > 0 && (
                    <>
                      , +{shippingRate.currency}{" "}
                      {parseFloat(shippingRate.extra_per_kg).toFixed(3)}/kg {isArabic ? 'لكل كيلوجرام إضافي' : 'for each additional kg'}
                    </>
                  )}
                </div>
              )}
              {!!selectedAddressData && selectedAddressMissingFields.length > 0 && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#991b1b",
                    padding: "8px 12px",
                    background: "#fee2e2",
                    borderRadius: "6px",
                    marginTop: "8px",
                  }}
                >
                  {isArabic
                    ? "العنوان المحدد غير مكتمل. يرجى إكمال: "
                    : "Selected address is incomplete. Please fill: "}
                  {getMissingFieldLabels(selectedAddressMissingFields, isArabic)}
                </div>
              )}
              <SummaryRow>
                <span>{isArabic ? 'رسوم الدفع الإلكتروني' : 'Online Payment Fee'}</span>
                <span>{onlinePaymentFee.toFixed(3)} {selectedCurrency}</span>
              </SummaryRow>
              <SummaryRow divider bold>
                <span>{isArabic ? 'المجموع' : 'Total'}</span>
                <span>{total.toFixed(3)} {selectedCurrency}</span>
              </SummaryRow>

              <PlaceOrderButton
                onClick={handlePlaceOrder}
                disabled={
                  isPlacingOrder ||
                  !cart ||
                  cartItems.length === 0 ||
                  !selectedAddress ||
                  calculatingShipping ||
                  !shippingRate ||
                  selectedAddressMissingFields.length > 0
                }
              >
                {isPlacingOrder
                  ? placingOrderMessage ||
                    (isArabic ? 'جاري تقديم الطلب...' : 'Placing Order...')
                  : isArabic
                    ? 'تقديم الطلب'
                    : 'Place Order'}
              </PlaceOrderButton>
            </>
          )}
        </Sidebar>
      </CheckoutContent>
    </Container>
  );
}

export default Checkout;
